import { create } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";

/** Filters must stay JSON-serializable: they are round-tripped through sessionStorage. */
export type TableFilters = Record<string, unknown>;

/** Which column a list is ordered by, and in which direction. */
export interface TableSort {
  by: string;
  order: "asc" | "desc";
}

export interface TableFiltersState<TFilters extends TableFilters> {
  /** The committed filter values — what the query is actually running with. */
  filters: TFilters;

  /** 1-based page number. Never restored from storage; a reload always opens on page 1. */
  page: number;

  /** Rows per page. Restored from storage — a preference for the tab, not a position. */
  pageSize: number;

  /**
   * Current ordering, or null for a table with no sort control. Cached like `pageSize`: it is a
   * preference, so it is deliberately not counted as an active filter.
   */
  sort: TableSort | null;

  /** Sets one filter and returns to page 1. */
  setFilter: <K extends keyof TFilters>(key: K, value: TFilters[K]) => void;

  /** Sets several filters at once and returns to page 1. */
  patchFilters: (patch: Partial<TFilters>) => void;

  setPage: (page: number) => void;

  /** Changing how many rows fit also invalidates the current page number. */
  setPageSize: (pageSize: number) => void;

  /**
   * Sorts by a column, the way a clickable table header behaves: the same column flips direction,
   * a different one starts descending. Returns to page 1 — page 3 of the old order is meaningless
   * in the new one. No-op for a table configured without `defaultSort`.
   */
  toggleSort: (by: string) => void;

  /** Clears every filter back to its default. Leaves `pageSize` alone — it is not a filter. */
  resetFilters: () => void;

  /**
   * Clears the filters, the page size and the cached copy in sessionStorage: the screen goes back
   * to exactly what a first-time visitor sees. This is the "forget everything" action, distinct
   * from `resetFilters`, which only clears what the operator can see on screen.
   */
  clearCache: () => void;

  /** True when at least one filter differs from its default. Page position is not a filter. */
  hasActiveFilters: () => boolean;

  /**
   * Bumped every time `resetFilters` or `clearCache` runs. A signal for components that keep their
   * own copy of a filter value — a debounced text box — so they can drop it when the operator
   * asks to start over.
   *
   * It exists because the filter values alone cannot carry that message. A box being typed into
   * has not written to the store yet, so a reset that lands while the term is uncommitted changes
   * nothing the component can observe: the store's `search` was already "" and stays "". The box
   * then keeps the abandoned term AND pushes it back when its debounce fires, which reads as the
   * reset never happening. Watching the count is the only way to tell "the operator reset this"
   * from "nothing changed". See UserFilters.
   *
   * Deliberately NOT persisted (see `partialize`): it is a within-session signal, and restoring a
   * count from storage would fire a phantom reset on load.
   */
  resetCount: number;
}

export interface CreateTableFiltersStoreOptions<TFilters extends TableFilters> {
  /**
   * sessionStorage key. Convention: `admin.<area>.filters` — the prefix keeps admin keys together
   * and distinct from the client app's, which may share an origin in a local dev proxy.
   */
  name: string;

  /** The cleared state. Also the shape gate: stored keys absent from here are discarded. */
  defaults: TFilters;

  /**
   * Bump whenever a filter is renamed, removed, or its accepted values change. A stored state from
   * an older shape is then dropped instead of being fed to the API, which would answer 400 for a
   * value that no longer exists — from a filter the operator cannot see, because the control that
   * set it is gone.
   */
  version?: number;

  /** Rows per page for a first-time visitor. Defaults to 50. */
  defaultPageSize?: number;

  /**
   * Starting order for a table that offers sorting. Omit for one that does not — `sort` then stays
   * null and `toggleSort` does nothing, so a table cannot accidentally send an order its endpoint
   * would ignore.
   */
  defaultSort?: TableSort;

  /**
   * Columns the endpoint accepts. A cached `sort.by` outside this list falls back to
   * `defaultSort`: the backend answers 400 (or silently reorders) for a column it does not know,
   * and a tab left open outlives the release that removed one.
   */
  allowedSortFields?: string[];

  /**
   * Page sizes the screen offers. A stored size outside this list is replaced by the default:
   * storage is user-editable, and the list endpoints reject a page size above 100.
   */
  allowedPageSizes?: number[];

  /**
   * Last say over a restored filter set, for what type checks cannot catch: an enum member that no
   * longer exists, a date range with only one end, an id that is no longer a GUID. Runs after
   * unknown keys are dropped and missing ones are filled from `defaults`.
   */
  sanitize?: (filters: TFilters) => TFilters;
}

const DEFAULT_PAGE_SIZES = [10, 20, 50, 100];

/**
 * Rows per page when a table does not say otherwise. 50 rather than 10 or 20: an operator scanning
 * for one tenant or one VM reads a screenful faster than they page through five of them, and the
 * list endpoints already batch their per-row lookups, so a larger page is one round trip, not
 * fifty.
 */
const DEFAULT_PAGE_SIZE = 50;

/**
 * sessionStorage that cannot throw.
 *
 * sessionStorage rather than localStorage: filters are the operator's working state for one tab.
 * They survive a reload and moving between screens, but a new tab or a new browser session opens
 * every list unfiltered — a date window or status set days ago must not quietly hide rows today.
 *
 * Reading or writing it raises in a Safari private window, when a browser is configured to block
 * site data, and when the origin's quota is full. None of that is a reason to take down an admin
 * screen, so every failure degrades to "this session just doesn't remember".
 */
const safeSessionStorage: StateStorage = {
  getItem: (name) => {
    try {
      return globalThis.sessionStorage?.getItem(name) ?? null;
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      globalThis.sessionStorage?.setItem(name, value);
    } catch {
      // Quota exceeded or storage blocked — the filters stay in memory for this session.
    }
  },
  removeItem: (name) => {
    try {
      globalThis.sessionStorage?.removeItem(name);
    } catch {
      // Same as above: nothing to recover, nothing worth failing over.
    }
  },
};

/** Compares one filter value against its default. Values are JSON, so this covers nested shapes. */
const isDefaultValue = (value: unknown, defaultValue: unknown): boolean =>
  JSON.stringify(value ?? null) === JSON.stringify(defaultValue ?? null);

/**
 * Shared filter + pagination state for an admin list screen, cached in sessionStorage.
 *
 * Every admin table repeats the same state: a handful of filters, a page number, a page size, an
 * optional sort column, and the rule that changing any of them sends the operator back to page 1. Written per page, that rule
 * gets forgotten on one setter and the table silently shows page 7 of a 2-page result. This factory
 * owns the rule once and hands each table a store.
 *
 * Usage:
 *
 * ```ts
 * // stores/tenantFiltersStore.ts
 * export const useTenantFiltersStore = createTableFiltersStore<TenantListFilters>({
 *   name: "admin.tenants.filters",
 *   version: 1,
 *   defaults: { search: "", status: "All" },
 *   defaultPageSize: 20,
 *   sanitize: (filters) => ({
 *     ...filters,
 *     status: STATUS_OPTIONS.some((o) => o.value === filters.status) ? filters.status : "All",
 *   }),
 * });
 * ```
 *
 * ```tsx
 * const { filters, page, pageSize, setFilter, setPage, resetFilters } = useTenantFiltersStore();
 * ```
 */
export function createTableFiltersStore<TFilters extends TableFilters>({
  name,
  defaults,
  version = 1,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  allowedPageSizes = DEFAULT_PAGE_SIZES,
  defaultSort,
  allowedSortFields,
  sanitize,
}: CreateTableFiltersStoreOptions<TFilters>) {
  const filterKeys = Object.keys(defaults) as (keyof TFilters)[];

  /**
   * Rebuilds a filter set from whatever was in storage: every key comes from `defaults`, so a key
   * that has since been removed is dropped and a key that has since been added gets its default.
   * Without this a rename leaves the old key sitting in the object forever, and a new filter reads
   * as `undefined` rather than as its default.
   */
  const restoreFilters = (stored: unknown): TFilters => {
    const source = (stored ?? {}) as Partial<TFilters>;

    const restored = filterKeys.reduce((accumulator, key) => {
      accumulator[key] = key in source && source[key] !== undefined
        ? (source[key] as TFilters[typeof key])
        : defaults[key];

      return accumulator;
    }, {} as TFilters);

    return sanitize ? sanitize(restored) : restored;
  };

  const restorePageSize = (stored: unknown): number =>
    typeof stored === "number" && allowedPageSizes.includes(stored) ? stored : defaultPageSize;

  const restoreSort = (stored: unknown): TableSort | null => {
    if (!defaultSort) {
      return null;
    }

    const candidate = stored as Partial<TableSort> | undefined;
    const byIsAllowed =
      typeof candidate?.by === "string" &&
      (allowedSortFields === undefined || allowedSortFields.includes(candidate.by));

    return byIsAllowed
      ? { by: candidate!.by!, order: candidate!.order === "asc" ? "asc" : "desc" }
      : { ...defaultSort };
  };

  return create<TableFiltersState<TFilters>>()(
    persist(
      (set, get) => ({
        filters: { ...defaults },
        page: 1,
        resetCount: 0,
        pageSize: defaultPageSize,
        sort: defaultSort ? { ...defaultSort } : null,

        setFilter: (key, value) =>
          set((state) => ({
            filters: { ...state.filters, [key]: value },
            // Page 1 on every filter change: the result set the current page pointed into no
            // longer exists, and staying put usually lands on an empty page.
            page: 1,
          })),

        patchFilters: (patch) =>
          set((state) => ({
            filters: { ...state.filters, ...patch },
            page: 1,
          })),

        setPage: (page) => set({ page }),

        setPageSize: (pageSize) => set({ pageSize, page: 1 }),

        toggleSort: (by) =>
          set((state) => {
            if (!state.sort) {
              return state;
            }

            return {
              sort:
                state.sort.by === by
                  ? { by, order: state.sort.order === "asc" ? "desc" : "asc" }
                  : { by, order: "desc" },
              page: 1,
            };
          }),

        resetFilters: () =>
          set((state) => ({ filters: { ...defaults }, page: 1, resetCount: state.resetCount + 1 })),

        clearCache: () => {
          set((state) => ({
            filters: { ...defaults },
            pageSize: defaultPageSize,
            sort: defaultSort ? { ...defaultSort } : null,
            page: 1,
            resetCount: state.resetCount + 1,
          }));

          // Removed after the state change, not before: persist writes synchronously on every set,
          // so clearing first would leave a freshly written entry behind.
          safeSessionStorage.removeItem(name);
        },

        hasActiveFilters: () => {
          const { filters } = get();

          return filterKeys.some((key) => !isDefaultValue(filters[key], defaults[key]));
        },
      }),
      {
        name,
        version,
        storage: createJSONStorage(() => safeSessionStorage),

        // Dropping the old state IS the migration: filters are a convenience, not data worth
        // carrying forward through a shape change. Declaring it matters — with no `migrate`,
        // zustand logs `console.error` on every load once the version moves, and leaves the stale
        // entry in storage to do it again next time. Returning undefined marks the load as
        // migrated, so the entry is rewritten at the new version and the noise stops.
        migrate: () => undefined,

        // Only these are worth remembering. The page number is deliberately absent: reloading onto
        // page 37 of a list whose rows have moved is disorienting, and on a shrunken result set
        // that page is empty.
        partialize: (state) => ({
          filters: state.filters,
          pageSize: state.pageSize,
          sort: state.sort,
        }),

        merge: (persisted, current) => {
          const stored = persisted as
            | Partial<{ filters: unknown; pageSize: unknown; sort: unknown }>
            | undefined;

          return {
            ...current,
            filters: restoreFilters(stored?.filters),
            pageSize: restorePageSize(stored?.pageSize),
            sort: restoreSort(stored?.sort),
            page: 1,
          };
        },
      },
    ),
  );
}
